import React, { Component } from 'react'
import { Link } from 'react-router'
import { connect } from 'react-redux'
import { push } from 'react-router-redux'
import uniqBy from 'lodash.uniqby'
import stringReplace from 'react-string-replace'

import classNames from './PresentChannel.css'
import { fetch } from '../utils/fetch'
import { createImageGetter } from '../utils/images'
import { userHasPerm } from '../utils/auth'
import { addImagesToEntities } from '../redux/search'
import { refreshTokens } from '../redux/auth'
import Presentation from '../components/present/Presentation'
import ChannelInfo from '../components/present/ChannelInfo'
import ChannelDebug from '../components/present/ChannelDebug'
import LoadingSpinner from '../components/LoadingSpinner'
import { PICASSO_SOCKET, ENDPOINT_PRESENT_TOKEN } from '../constants'

const DEBUG_TIMEOUT = 10 // Time to wait for images before showing logs
const REFRESH_TOKENS_INTERVAL = 82800000 // 23 hours

class PresentChannel extends Component {
  state = {
    images: [],
    logs: [],
    conflicts: [],
    showDebug: false,
    showDebugOverlay: false,
    showInfoOverlay: false,
    isOwner: false,
    alert: null,
    imagesPlayedCount: null
  }
  log = (message, source = 'frontend') => {
    if (this.mounted) {
      console.log(source, message)
      this.setState({
        logs: this.state.logs.concat({ message, source })
      })
    }
  }
  componentDidMount () {
    this.mounted = true
    const { userID } = this.props
    this.log('Getting auth token from picasso')
    this.props.getToken().then(auth => {
      if (auth) {
        this.log('Token received')
        this.openSocket({ token: auth.token, user: userID })
      } else {
        this.log('No token returned from /present/token')
      }
    })
    this.refreshInterval = setInterval(this.props.refreshTokens, REFRESH_TOKENS_INTERVAL)
  }
  componentWillUnmount () {
    this.ws && this.ws.close()
    this.mounted = false
    clearInterval(this.refreshInterval)
  }
  openSocket = auth => {
    this.log('Connecting to websocket')
    this.ws = new window.WebSocket(PICASSO_SOCKET)
    this.ws.onopen = () => {
      this.log('Socket open, logging in')
      this.login(auth.user, auth.token)
    }
    this.ws.onmessage = this.onSocketMessage
    this.ws.onclose = e => {
      this.log('Socket closed')
      if (this.mounted) {
        this.setState({ showDebug: true })
      }
    }
    this.ws.onerror = e => {
      this.log('Socket error')
      if (this.mounted) {
        this.setState({ showDebug: true })
      }
    }
    window.ws = this.ws // TMP
  }
  sendMessage = data => {
    this.ws.send(JSON.stringify(data))
  }
  login = (user, token) => {
    this.sendMessage({
      type: 'login',
      instance_id: this.props.params.instance_id,
      state: this.props.location.query.debug || this.props.params.task === 'info' ? 'absent' : 'present',
      user,
      token,
      time_offset: new Date().getTimezoneOffset() * -60
    })
  }
  requestInfo = () => {
    this.sendMessage({
      type: 'info',
      action: 'data'
    })
  }
  sendMod = (action, extra) => {
    this.sendMessage({
      type: 'mod',
      action,
      ...extra
    })
  }
  sendEvent = action => {
    this.sendMessage({ type: 'event', action })
  }
  onSocketMessage = e => {
    const message = JSON.parse(e.data)
    if (message.error) {
      this.log('Error: ' + message.error, 'picasso')
      this.setState({ showDebug: true })
    }
    if (message.alert) {
      this.log('Alert: ' + message.alert, 'picasso')
      if (message.key === 'channel_finished_owner') {
        // Channel is finished, so show the alert after last image has played
        const { channel } = this.state
        const delay = channel.playing_album.presentation_speed + channel.playing_album.presentation_transition
        setTimeout(() => {
          this.setState({ alert: message.alert })
        }, delay * 1000)
      } else {
        this.setState({ alert: message.alert })
      }
      clearTimeout(this.debugTimeout)
    }
    if (message.images) {
      if (message.images.length === 0) {
        if (message.queue.state === 'played') {
          this.onFinish()
        }
      } else {
        this.onReceiveImages(message)
      }
    }
    if (message.user) {
      this.log(`User state: ${message.user.state}`, 'picasso')
      message.user.why && this.log(`User state reason: ${message.user.why}`, 'picasso')
      if (message.user.state === 'online') {
        this.onAuthed()
        this.log('User authorized')
      }
      this.setState({
        isOwner: message.user.owner
      })
    }
    if (message.channel) {
      if (message.channel.data) {
        this.setState({
          info: message.channel.data
        })
      }
    }
    if (message.conflicts) {
      this.setState({
        conflicts: message.conflicts
      })
    }
    if (message.event && this.refs.presentation) {
      if (message.event.action === 'play') {
        this.refs.presentation.play()
      }
      if (message.event.action === 'pause') {
        this.refs.presentation.pause()
      }
      if (message.event.action === 'prev-image') {
        this.refs.presentation.prevSlide()
      }
      if (message.event.action === 'next-image') {
        this.refs.presentation.nextSlide()
      }
      if (message.event.action === 'next') {
        this.onSkip()
      }
    }
    if (message.debug) {
      message.debug.forEach(message => this.log(message, 'picasso'))
    }
  }
  onAuthed = () => {
    const { params } = this.props
    if (params.task === 'info') {
      this.requestInfo()
      this.sendMessage({ type: 'validation' })
    } else {
      // Play message is no longer required here
      this.debugTimeout = setTimeout(() => this.setState({ showDebug: true }), DEBUG_TIMEOUT * 1000)
    }
  }
  onReceiveImages = message => {
    this.log(`Received ${message.images.length} images`)
    clearTimeout(this.debugTimeout)

    this.props.addImagesToEntities(message.images)

    this.setState({
      channel: message.channel,
      showDebug: false
    })

    if (this.state.imagesPlayedCount === null) {
      this.setState({
        imagesPlayedCount: Math.max(0, message.channel.progress.total_images_played - message.images.length)
      })
    }

    // If the playlist/album has changed, reset images to this new array
    if (!this.state.channel || message.channel.playing_album.id !== this.state.channel.playing_album.id) {
      this.setState({
        images: message.images
      })
      return
    }

    // Otherwise, append images to the current array
    this.setState({
      images: uniqBy(this.state.images.concat(message.images), '_id')
    }, this.resolveOnImages)
  }
  loadMoreImages = () => {
    return new Promise(resolve => {
      this.sendMod('consume_queue')
      this.resolveOnImages = resolve
    })
  }
  onSkip = () => {
    this.sendMod('play_next_album')
  }
  onReset = () => {
    this.sendMod('reset_queue')
  }
  onPause = () => {
    this.sendMessage({ type: 'state', state: 'absent' })
  }
  onPlay = () => {
    this.sendMessage({ type: 'state', state: 'present' })
  }
  onClose = () => {
    if (this.props.shouldGoBack) {
      window.history.back()
    } else {
      this.props.goHome()
    }
  }
  onToggleSkip = e => {
    this.sendMod('skip_album', { album_id: e.target.value })
    this.requestInfo()
  }
  onToggleUnskip = e => {
    this.sendMod('unmute_album', { album_id: e.target.value })
    this.requestInfo()
  }
  onFinish = () => {
    window.history.back()
  }
  render () {
    const { location, params, getImageSource } = this.props
    const { images, channel, info, conflicts, alert, isOwner } = this.state
    if (params.task === 'info') {
      if (!info) {
        return <LoadingSpinner />
      }
      return (
        <ChannelInfo
          data={info}
          conflicts={conflicts}
          onSkip={this.onToggleSkip}
          onUnskip={this.onToggleUnskip}
          getImageSource={getImageSource}
          isOwner={isOwner}
        />
      )
    }

    if (alert) {
      const backLink = match => <Link to='/channels'>{match}</Link>
      const reloadButton = match => <button onClick={() => window.location.reload()}>{match}</button>
      let text = stringReplace(alert, 'go back to channels', backLink)
      text = stringReplace(text, 'reloading the page', reloadButton)
      return (
        <div className={classNames.blackOverlay}>
          <p>
            {text}
          </p>
          {!this.props.anonymousUser && /back to channels/.test(alert) === false &&
            <Link to='/channels' className={classNames.button}>
              Back to Channels
            </Link>
          }
        </div>
      )
    }

    if (location.query.debug || this.state.showDebug) {
      return (
        <ChannelDebug logs={this.state.logs} />
      )
    }

    if (images.length === 0) {
      return (
        <div className={classNames.blackOverlay}>
          Loading your images…
          <LoadingSpinner style={{ opacity: 0.8 }} />
        </div>
      )
    }

    return (
      <Presentation
        ref='presentation'
        key={channel.playing_album.id}
        type={channel.playing_album.presentation_type}
        options={{
          delay: channel.playing_album.presentation_speed,
          transition: channel.playing_album.presentation_transition
        }}
        images={images.map(image => image._id)}
        count={channel.playing_album.album_count}
        showTotalCount={channel.progress.total_images}
        imagesPlayedCount={this.state.imagesPlayedCount}
        getImageSource={getImageSource}
        loadMoreImages={this.loadMoreImages}
        onClose={this.onClose}
        onSkip={isOwner && this.onSkip}
        onStartAgain={isOwner && this.onReset}
        onPause={this.onPause}
        onPlay={this.onPlay}
        onFinish={this.loadMoreImages}
        entities={this.props.entities}
        albumName={channel.playing_album.name}
        playlistName={channel.playing_playlist.name}
        queueName={channel.queue.name}
        channelInfo={
          <ChannelInfo
            data={info}
            conflicts={conflicts}
            onSkip={this.onToggleSkip}
            onUnskip={this.onToggleUnskip}
            getImageSource={getImageSource}
            isOwner={isOwner}
            padded
          />
        }
        anonymousUser={this.props.anonymousUser}
        channelDebug={<ChannelDebug logs={this.state.logs} />}
        canShareImagesInternal={this.props.canShareImagesInternal}
        canShareImagesExternal={this.props.canShareImagesExternal}
        presentTypeSelected={this.props.presentTypeSelected}
        showInfoOverlay={this.state.showInfoOverlay}
        onShowInfoOverlay={() => {
          this.requestInfo()
          this.setState({ showInfoOverlay: true })
        }}
        onHideInfoOverlay={() => this.setState({ showInfoOverlay: false })}
        showDebugOverlay={this.state.showDebugOverlay}
        onShowDebugOverlay={() => this.setState({ showDebugOverlay: true })}
        onHideDebugOverlay={() => this.setState({ showDebugOverlay: false })}
      />
    )
  }
}

const mapStateToProps = ({ queues, entities, auth, form, nav }, { params }) => ({
  entities,
  getToken: () => fetch(ENDPOINT_PRESENT_TOKEN, {
    method: 'post',
    token: auth.token
  }),
  getImageSource: createImageGetter(auth),
  userID: auth.user && auth.user.id,
  anonymousUser: auth.user && auth.user.id === 0,
  canShareImagesInternal: userHasPerm(auth.user, 'internal_share'),
  canShareImagesExternal: userHasPerm(auth.user, 'external_share'),
  presentTypeSelected: form.present && form.present.values && form.present.values.presentation_type,
  shouldGoBack: /\/login/.test(nav.previousPath) === false
})

const mapDispatchToProps = {
  addImagesToEntities,
  refreshTokens,
  goHome: id => dispatch => dispatch(push('/'))
}

export default connect(mapStateToProps, mapDispatchToProps)(PresentChannel)



// WEBPACK FOOTER //
// ./src/pages/PresentChannel.js